import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuthContext} from "../services/auth.service";
import {useDataContext} from "../services/data.service";
import useSWR, {mutate} from "swr";
import axios from "axios";
import {API_URL} from "../services/api.service";
import {toast} from "react-hot-toast";
import Button from "../components/_core/Button";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCubes, faPlus, faMinus, faTrash, faPencil, faLayerGroup, faCube} from "@fortawesome/free-solid-svg-icons";

const ObjectManagementPage = () => {
    const {user, token} = useAuthContext();
    const {setModal, setModalOpen} = useDataContext();
    const navigate = useNavigate();

    // Tab state: 'types' or 'objects'
    const [activeTab, setActiveTab] = useState('types');

    // Object Types state
    const [showAddTypeForm, setShowAddTypeForm] = useState(false);
    const [newTypeName, setNewTypeName] = useState("");
    const [newTypeDesc, setNewTypeDesc] = useState("");
    const [editingTypeId, setEditingTypeId] = useState(null);
    const [editingTypeName, setEditingTypeName] = useState("");
    const [editingTypeDesc, setEditingTypeDesc] = useState("");
    const [deleteTypeConfirmId, setDeleteTypeConfirmId] = useState(null);

    // Objects state
    const [showAddObjectForm, setShowAddObjectForm] = useState(false);
    const [newObjectName, setNewObjectName] = useState("");
    const [newObjectType, setNewObjectType] = useState("");
    const [newObjectDesc, setNewObjectDesc] = useState("");
    const [editingObjectId, setEditingObjectId] = useState(null);
    const [editingObjectName, setEditingObjectName] = useState("");
    const [editingObjectType, setEditingObjectType] = useState("");
    const [editingObjectDesc, setEditingObjectDesc] = useState("");
    const [deleteObjectConfirmId, setDeleteObjectConfirmId] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const OBJECT_TYPES_URL = `${API_URL}/object-types`;
    const OBJECTS_URL = `${API_URL}/objects`;

    const getObjectTypes = () => axios.get(OBJECT_TYPES_URL, {
        headers: {"Authorization": `Bearer ${token}`}
    }).then(res => res.data);

    const getObjects = () => axios.get(OBJECTS_URL, {
        headers: {"Authorization": `Bearer ${token}`}
    }).then(res => res.data);

    const {data: objectTypes, error: objectTypesError} = useSWR(token ? OBJECT_TYPES_URL : null, getObjectTypes);
    const {data: objects, error: objectsError} = useSWR(token ? OBJECTS_URL : null, getObjects);

    useEffect(() => {
        if (user && user.user_type !== 3) {
            navigate("/datasets");
        }
    }, [user, navigate]);

    useEffect(() => {
        document.title = "GT2 - Object Management";
    }, []);

    // Set default object type when types are loaded
    useEffect(() => {
        if (objectTypes && objectTypes.length > 0 && !newObjectType) {
            setNewObjectType(objectTypes[0].id);
        }
    }, [objectTypes, newObjectType]);

    if (objectTypesError) console.error(objectTypesError);
    if (objectsError) console.error(objectsError);

    // ===== Object Types CRUD =====
    const handleAddType = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await axios.post(OBJECT_TYPES_URL, {
                name: newTypeName,
                desc: newTypeDesc
            }, {
                headers: {"Authorization": `Bearer ${token}`}
            });

            if (response.data.success) {
                toast.success("Object type created successfully!", {position: "top-center"});
                setNewTypeName("");
                setNewTypeDesc("");
                setShowAddTypeForm(false);
                mutate(OBJECT_TYPES_URL);
            } else {
                toast.error(response.data.error || "Failed to create object type", {position: "top-center"});
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to create object type", {position: "top-center"});
        } finally {
            setIsSubmitting(false);
        }
    };

    const startEditingType = (t) => {
        setEditingTypeId(t.id);
        setEditingTypeName(t.name);
        setEditingTypeDesc(t.desc || "");
    };

    const cancelEditingType = () => {
        setEditingTypeId(null);
        setEditingTypeName("");
        setEditingTypeDesc("");
    };

    const handleUpdateType = async (typeId) => {
        try {
            const response = await axios.patch(`${OBJECT_TYPES_URL}/${typeId}`, {
                name: editingTypeName,
                desc: editingTypeDesc
            }, {
                headers: {"Authorization": `Bearer ${token}`}
            });

            if (response.data.success) {
                toast.success("Object type updated successfully!", {position: "top-center"});
                cancelEditingType();
                mutate(OBJECT_TYPES_URL);
            } else {
                toast.error(response.data.error || "Failed to update object type", {position: "top-center"});
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update object type", {position: "top-center"});
        }
    };

    const handleDeleteType = async (typeId) => {
        try {
            const response = await axios.delete(`${OBJECT_TYPES_URL}/${typeId}`, {
                headers: {"Authorization": `Bearer ${token}`}
            });

            if (response.data.success) {
                toast.success("Object type deleted successfully!", {position: "top-center"});
                setDeleteTypeConfirmId(null);
                mutate(OBJECT_TYPES_URL);
            } else {
                toast.error(response.data.error || "Failed to delete object type", {position: "top-center"});
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to delete object type", {position: "top-center"});
        }
    };

    // ===== Objects CRUD =====
    const handleAddObject = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await axios.post(OBJECTS_URL, {
                name: newObjectName,
                type: parseInt(newObjectType),
                desc: newObjectDesc
            }, {
                headers: {"Authorization": `Bearer ${token}`}
            });

            if (response.data.success) {
                toast.success("Object created successfully!", {position: "top-center"});
                setNewObjectName("");
                setNewObjectDesc("");
                setShowAddObjectForm(false);
                mutate(OBJECTS_URL);
            } else {
                toast.error(response.data.error || "Failed to create object", {position: "top-center"});
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to create object", {position: "top-center"});
        } finally {
            setIsSubmitting(false);
        }
    };

    const startEditingObject = (o) => {
        setEditingObjectId(o.id);
        setEditingObjectName(o.name);
        setEditingObjectType(o.type);
        setEditingObjectDesc(o.desc || "");
    };

    const cancelEditingObject = () => {
        setEditingObjectId(null);
        setEditingObjectName("");
        setEditingObjectType("");
        setEditingObjectDesc("");
    };

    const handleUpdateObject = async (objectId) => {
        try {
            const response = await axios.patch(`${OBJECTS_URL}/${objectId}`, {
                name: editingObjectName,
                type: parseInt(editingObjectType),
                desc: editingObjectDesc
            }, {
                headers: {"Authorization": `Bearer ${token}`}
            });

            if (response.data.success) {
                toast.success("Object updated successfully!", {position: "top-center"});
                cancelEditingObject();
                mutate(OBJECTS_URL);
            } else {
                toast.error(response.data.error || "Failed to update object", {position: "top-center"});
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update object", {position: "top-center"});
        }
    };

    const handleDeleteObject = async (objectId) => {
        try {
            const response = await axios.delete(`${OBJECTS_URL}/${objectId}`, {
                headers: {"Authorization": `Bearer ${token}`}
            });

            if (response.data.success) {
                toast.success("Object deleted successfully!", {position: "top-center"});
                setDeleteObjectConfirmId(null);
                mutate(OBJECTS_URL);
            } else {
                toast.error(response.data.error || "Failed to delete object", {position: "top-center"});
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to delete object", {position: "top-center"});
        }
    };

    const getTypeName = (typeId) => {
        if (!objectTypes) return "Unknown";
        const type = objectTypes.find(t => t.id === typeId);
        return type ? type.name : "Unknown";
    };

    const loadingTypes = !objectTypes;
    const loadingObjects = !objects;

    const tabClasses = "px-6 py-3 text-sm font-semibold transition-colors";
    const activeTabClasses = `${tabClasses} bg-white text-blue-600 border-b-2 border-blue-600`;
    const inactiveTabClasses = `${tabClasses} bg-slate-100 text-slate-600 hover:bg-slate-200`;

    return (
        <div className="w-full h-full flex flex-col">
            <div className="flex justify-between items-end bg-slate-100 py-6 px-8">
                <h2 className="text-4xl font-bold">
                    <FontAwesomeIcon icon={faCubes} className="mr-2" />
                    Object Management
                </h2>
            </div>

            {/* Tabs */}
            <div className="flex bg-slate-200 border-b border-slate-300">
                <button
                    className={activeTab === 'types' ? activeTabClasses : inactiveTabClasses}
                    onClick={() => setActiveTab('types')}
                >
                    <FontAwesomeIcon icon={faLayerGroup} className="mr-2" />
                    Object Types
                </button>
                <button
                    className={activeTab === 'objects' ? activeTabClasses : inactiveTabClasses}
                    onClick={() => setActiveTab('objects')}
                >
                    <FontAwesomeIcon icon={faCube} className="mr-2" />
                    Objects
                </button>
            </div>

            {/* Object Types Tab */}
            {activeTab === 'types' && (
                <>
                    <div className="flex justify-between items-center bg-white px-8 py-4 border-b border-slate-200">
                        <div className="flex items-center gap-4">
                            {!loadingTypes && <p className="text-gray-500 text-sm">{objectTypes.length} object type(s)</p>}
                        </div>
                        <Button onClick={() => setShowAddTypeForm(!showAddTypeForm)}>
                            <FontAwesomeIcon icon={showAddTypeForm ? faMinus : faPlus} className="mr-2" />
                            {showAddTypeForm ? "Cancel" : "Add Object Type"}
                        </Button>
                    </div>

                    {showAddTypeForm && (
                        <div className="bg-slate-50 px-8 py-4 border-b border-slate-200">
                            <h3 className="text-lg font-semibold mb-4">Add New Object Type</h3>
                            <form onSubmit={handleAddType} className="flex flex-wrap gap-4 items-end">
                                <div className="flex flex-col">
                                    <label className="text-sm text-gray-600 mb-1">Name *</label>
                                    <input
                                        type="text"
                                        value={newTypeName}
                                        onChange={(e) => setNewTypeName(e.target.value)}
                                        className="border border-gray-300 rounded px-3 py-2 w-48"
                                        maxLength={45}
                                        required
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className="text-sm text-gray-600 mb-1">Description</label>
                                    <input
                                        type="text"
                                        value={newTypeDesc}
                                        onChange={(e) => setNewTypeDesc(e.target.value)}
                                        className="border border-gray-300 rounded px-3 py-2 w-64"
                                        maxLength={200}
                                    />
                                </div>
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? "Creating..." : "Create Type"}
                                </Button>
                            </form>
                        </div>
                    )}

                    <div className="flex items-center bg-slate-200 text-slate-500 text-sm uppercase tracking-wider font-bold justify-between px-8 py-3 top-0 shadow-lg z-10">
                        <div className="w-16">ID</div>
                        <div className="w-64">Name</div>
                        <div className="flex-1">Description</div>
                        <div className="w-48 text-right"></div>
                    </div>

                    <div className="flex-grow overflow-y-auto py-4" id="object-types">
                        <div className="flex flex-col gap-4">
                            {loadingTypes ? (
                                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                                    <p className="text-lg">Loading object types...</p>
                                </div>
                            ) : objectTypes.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                                    <FontAwesomeIcon icon={faLayerGroup} className="text-5xl mb-4 text-gray-300" />
                                    <p className="text-lg">No object types found</p>
                                    <p className="text-sm mt-2">Click "Add Object Type" to create one</p>
                                </div>
                            ) : objectTypes.map(t => (
                                <div key={t.id} className="flex items-center justify-between rounded px-4 py-2 mx-4 hover:bg-slate-50 bg-slate-100 transition-colors duration-150">
                                    <div className="w-16">{t.id}</div>
                                    <div className="w-64">
                                        {editingTypeId === t.id ? (
                                            <input
                                                type="text"
                                                value={editingTypeName}
                                                onChange={(e) => setEditingTypeName(e.target.value)}
                                                className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                                                maxLength={45}
                                            />
                                        ) : (
                                            t.name
                                        )}
                                    </div>
                                    <div className="flex-1 pr-4">
                                        {editingTypeId === t.id ? (
                                            <input
                                                type="text"
                                                value={editingTypeDesc}
                                                onChange={(e) => setEditingTypeDesc(e.target.value)}
                                                className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                                                maxLength={200}
                                            />
                                        ) : (
                                            <span className="text-gray-600">{t.desc || "-"}</span>
                                        )}
                                    </div>
                                    <div className="w-48 flex items-center justify-end gap-2">
                                        {editingTypeId === t.id ? (
                                            <>
                                                <Button size="small" onClick={() => handleUpdateType(t.id)}>
                                                    Save
                                                </Button>
                                                <Button size="small" style="secondary" onClick={cancelEditingType}>
                                                    Cancel
                                                </Button>
                                            </>
                                        ) : deleteTypeConfirmId === t.id ? (
                                            <>
                                                <Button size="small" onClick={() => handleDeleteType(t.id)}>
                                                    Confirm
                                                </Button>
                                                <Button size="small" style="secondary" onClick={() => setDeleteTypeConfirmId(null)}>
                                                    Cancel
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    size="small"
                                                    style="secondary"
                                                    onClick={() => startEditingType(t)}
                                                    title="Edit object type"
                                                >
                                                    <FontAwesomeIcon icon={faPencil} />
                                                </Button>
                                                <Button
                                                    size="small"
                                                    style="secondary"
                                                    onClick={() => setDeleteTypeConfirmId(t.id)}
                                                    title="Delete object type"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Objects Tab */}
            {activeTab === 'objects' && (
                <>
                    <div className="flex justify-between items-center bg-white px-8 py-4 border-b border-slate-200">
                        <div className="flex items-center gap-4">
                            {!loadingObjects && <p className="text-gray-500 text-sm">{objects.length} object(s)</p>}
                        </div>
                        <Button onClick={() => setShowAddObjectForm(!showAddObjectForm)}>
                            <FontAwesomeIcon icon={showAddObjectForm ? faMinus : faPlus} className="mr-2" />
                            {showAddObjectForm ? "Cancel" : "Add Object"}
                        </Button>
                    </div>

                    {showAddObjectForm && (
                        <div className="bg-slate-50 px-8 py-4 border-b border-slate-200">
                            <h3 className="text-lg font-semibold mb-4">Add New Object</h3>
                            <form onSubmit={handleAddObject} className="flex flex-wrap gap-4 items-end">
                                <div className="flex flex-col">
                                    <label className="text-sm text-gray-600 mb-1">Name *</label>
                                    <input
                                        type="text"
                                        value={newObjectName}
                                        onChange={(e) => setNewObjectName(e.target.value)}
                                        className="border border-gray-300 rounded px-3 py-2 w-48"
                                        maxLength={45}
                                        required
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className="text-sm text-gray-600 mb-1">Type *</label>
                                    <select
                                        value={newObjectType}
                                        onChange={(e) => setNewObjectType(e.target.value)}
                                        className="border border-gray-300 rounded px-3 py-2 w-48"
                                        required
                                    >
                                        {objectTypes && objectTypes.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex flex-col">
                                    <label className="text-sm text-gray-600 mb-1">Description</label>
                                    <input
                                        type="text"
                                        value={newObjectDesc}
                                        onChange={(e) => setNewObjectDesc(e.target.value)}
                                        className="border border-gray-300 rounded px-3 py-2 w-64"
                                        maxLength={200}
                                    />
                                </div>
                                <Button type="submit" disabled={isSubmitting || !objectTypes || objectTypes.length === 0}>
                                    {isSubmitting ? "Creating..." : "Create Object"}
                                </Button>
                            </form>
                        </div>
                    )}

                    <div className="flex items-center bg-slate-200 text-slate-500 text-sm uppercase tracking-wider font-bold justify-between px-8 py-3 top-0 shadow-lg z-10">
                        <div className="w-16">ID</div>
                        <div className="w-64">Name</div>
                        <div className="w-48">Type</div>
                        <div className="flex-1">Description</div>
                        <div className="w-48 text-right"></div>
                    </div>

                    <div className="flex-grow overflow-y-auto py-4" id="objects">
                        <div className="flex flex-col gap-4">
                            {loadingObjects ? (
                                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                                    <p className="text-lg">Loading objects...</p>
                                </div>
                            ) : objects.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                                    <FontAwesomeIcon icon={faCube} className="text-5xl mb-4 text-gray-300" />
                                    <p className="text-lg">No objects found</p>
                                    <p className="text-sm mt-2">Click "Add Object" to create one</p>
                                </div>
                            ) : objects.map(o => (
                                <div key={o.id} className="flex items-center justify-between rounded px-4 py-2 mx-4 hover:bg-slate-50 bg-slate-100 transition-colors duration-150">
                                    <div className="w-16">{o.id}</div>
                                    <div className="w-64">
                                        {editingObjectId === o.id ? (
                                            <input
                                                type="text"
                                                value={editingObjectName}
                                                onChange={(e) => setEditingObjectName(e.target.value)}
                                                className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                                                maxLength={45}
                                            />
                                        ) : (
                                            o.name
                                        )}
                                    </div>
                                    <div className="w-48">
                                        {editingObjectId === o.id ? (
                                            <select
                                                value={editingObjectType}
                                                onChange={(e) => setEditingObjectType(e.target.value)}
                                                className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                                            >
                                                {objectTypes && objectTypes.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-700">
                                                {getTypeName(o.type)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex-1 pr-4">
                                        {editingObjectId === o.id ? (
                                            <input
                                                type="text"
                                                value={editingObjectDesc}
                                                onChange={(e) => setEditingObjectDesc(e.target.value)}
                                                className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                                                maxLength={200}
                                            />
                                        ) : (
                                            <span className="text-gray-600">{o.desc || "-"}</span>
                                        )}
                                    </div>
                                    <div className="w-48 flex items-center justify-end gap-2">
                                        {editingObjectId === o.id ? (
                                            <>
                                                <Button size="small" onClick={() => handleUpdateObject(o.id)}>
                                                    Save
                                                </Button>
                                                <Button size="small" style="secondary" onClick={cancelEditingObject}>
                                                    Cancel
                                                </Button>
                                            </>
                                        ) : deleteObjectConfirmId === o.id ? (
                                            <>
                                                <Button size="small" onClick={() => handleDeleteObject(o.id)}>
                                                    Confirm
                                                </Button>
                                                <Button size="small" style="secondary" onClick={() => setDeleteObjectConfirmId(null)}>
                                                    Cancel
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    size="small"
                                                    style="secondary"
                                                    onClick={() => startEditingObject(o)}
                                                    title="Edit object"
                                                >
                                                    <FontAwesomeIcon icon={faPencil} />
                                                </Button>
                                                <Button
                                                    size="small"
                                                    style="secondary"
                                                    onClick={() => setDeleteObjectConfirmId(o.id)}
                                                    title="Delete object"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ObjectManagementPage;
